// LeadsForecastPanel — Phase 5 of the Leads list-view redesign.
//
// Combines two of the spec sections into one collapsible panel:
//   Section 9  — Pipeline Forecast
//   Section 12 — AI Predictions
//   Section 8  — AI Performance
//
// One fetch to /api/leads/forecast. Renders as a 3-tile row that
// answers three questions:
//
//   1. Where's my revenue heading?          (Forecast)
//   2. What should I bet on / avoid?        (Predictions)
//   3. How am I performing today?           (Performance — derived)
//
// Collapsible so it doesn't crowd the top of the page for agents
// who prefer to focus on the queue.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt$(cents) {
  if (!Number.isFinite(cents) || cents === 0) return '$0';
  if (cents >= 1_000_000) return `$${(cents / 100_000_000).toFixed(1)}M`;
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

const HOUR_LABEL = ['12a','1a','2a','3a','4a','5a','6a','7a','8a','9a','10a','11a','12p','1p','2p','3p','4p','5p','6p','7p','8p','9p','10p','11p'];

function VerticalIcon({ v }) {
  if (v === 'life') return <span>💼</span>;
  if (v === 'medicare' || v === 'senior') return <span>🩺</span>;
  return <span>❤️</span>;
}

export default function LeadsForecastPanel({ leads, calls }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const [expanded, setExpanded] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/leads/forecast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await bearer()) },
          body: JSON.stringify({
            leads: (leads || []).slice(0, 500),
            callHistory: (calls || []).slice(0, 500),
          }),
        });
        const j = await r.json();
        if (cancelled) return;
        if (r.ok && j.ok) setState({ loading: false, data: j, error: null });
        else setState({ loading: false, data: null, error: j.error || `HTTP ${r.status}` });
      } catch (err) {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, [leads, calls]);

  if (state.loading || state.error || !state.data) return null;
  const d = state.data;

  // Performance — derived on the client, no round trip.
  const today = new Date().toISOString().slice(0, 10);
  const callsToday = (calls || []).filter(c => (c.date || '').startsWith(today));
  const talkMinToday = callsToday.reduce((sum, c) => sum + (Number(c.durationSec || 0) / 60), 0);
  const answeredToday = callsToday.filter(c => /answer|interested|appt|talk|conn/i.test(String(c.outcome || ''))).length;
  const contactRate = callsToday.length > 0 ? Math.round(100 * answeredToday / callsToday.length) : null;

  const h7  = d.forecast.horizons.find(h => h.horizon_days === 7)  || {};
  const h30 = d.forecast.horizons.find(h => h.horizon_days === 30) || {};

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      padding: 12,
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#0891b2', fontWeight: 800 }}>
          🔮 Forecast · Predictions · Performance
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          {d.forecast.confidence}% confidence · {d.basis.sample_closes} closes basis
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#0891b2', fontWeight: 700 }}>
          {expanded ? 'Hide' : 'Show'}
        </div>
      </button>

      {expanded && (
        <div style={{
          marginTop: 12,
          display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}>
          {/* Forecast card */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#475569', fontWeight: 800, marginBottom: 6 }}>Next 30d forecast</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#059669', fontFamily: 'JetBrains Mono, monospace' }}>
              {fmt$(h30.projected_commission_cents || 0)}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              {h30.projected_closes || 0} closes projected
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6, borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
              Needs: <b>{h30.appointments_needed || 0}</b> appts · <b>{(h30.calls_needed || 0).toLocaleString()}</b> calls
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
              7d: {fmt$(h7.projected_commission_cents || 0)}
            </div>
          </div>

          {/* Predictions card */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#475569', fontWeight: 800, marginBottom: 6 }}>AI predictions</div>
            {d.predictions.highest_opportunity_vertical && (
              <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6 }}>
                <span style={{ color: '#64748b', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Best bet</span><br/>
                <VerticalIcon v={d.predictions.highest_opportunity_vertical}/>{' '}
                <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>{d.predictions.highest_opportunity_vertical}</span> pipeline
              </div>
            )}
            {d.predictions.highest_risk_source && (
              <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6 }}>
                <span style={{ color: '#64748b', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Highest risk</span><br/>
                <span>⚠️ </span><span style={{ fontWeight: 700 }}>{d.predictions.highest_risk_source}</span> source
              </div>
            )}
            {d.predictions.best_call_hour != null && (
              <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6 }}>
                <span style={{ color: '#64748b', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Best call time</span><br/>
                <span>🕒 </span><span style={{ fontWeight: 700 }}>{HOUR_LABEL[d.predictions.best_call_hour]}</span> historically
              </div>
            )}
            {!d.predictions.highest_opportunity_vertical && !d.predictions.highest_risk_source && d.predictions.best_call_hour == null && (
              <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginTop: 6 }}>
                More data needed — predictions unlock at ~5 closes.
              </div>
            )}
          </div>

          {/* Performance card */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#475569', fontWeight: 800, marginBottom: 6 }}>Your performance today</div>
            <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <div><span style={{ color: '#64748b' }}>Calls: </span><span style={{ fontWeight: 700, color: '#0f172a' }}>{callsToday.length}</span></div>
              <div><span style={{ color: '#64748b' }}>Talk time: </span><span style={{ fontWeight: 700, color: '#0f172a' }}>{Math.round(talkMinToday)}m</span></div>
              <div><span style={{ color: '#64748b' }}>Answered: </span><span style={{ fontWeight: 700, color: '#0f172a' }}>{answeredToday}</span></div>
              {contactRate != null && (
                <div><span style={{ color: '#64748b' }}>Contact rate: </span><span style={{ fontWeight: 700, color: contactRate >= 40 ? '#059669' : '#dc2626' }}>{contactRate}%</span></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
